import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Loader2, Users, Plus, X, Check, DollarSign } from "lucide-react";

const HB_ICON = "https://legends.ml/static/images/lib/skill/1301007.png";
const HP_GEAR_ICON = "https://royals-library.netlify.app/images/skills/0001001.png";
const SAFE_RUN_ICON = "https://royals-library.netlify.app/images/skills/2321006.png";
const APR_ICON = "https://legends.ml/static/images/lib/item/05050000.png";
const ONYX_APPLE_ICON = "https://royals-library.netlify.app/images/items/02022179.png";

export default function CreateExpeditionModal({
  showCreateExpedition, setShowCreateExpedition, expeditionForm, setExpeditionForm,
  handleCreateExpedition, actionLoading, userCharacters, availableJobs, expeditionBosses,
  expeditionWishes, formatMesos, hunterLfgPosts, removeJobRequirement, newJobReq, setNewJobReq, addJobRequirement
}) {
  return (
      <Dialog open={showCreateExpedition} onOpenChange={setShowCreateExpedition}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" style={{ background: "linear-gradient(180deg, #0d1b4a 0%, #0a0f2c 100%)", border: "2px solid #3949ab" }}>
          <DialogHeader>
            <DialogTitle className="text-blue-300 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Create Expedition (LFG)
            </DialogTitle>
            <DialogDescription className="text-blue-200/60">
              Set up your party and specify which jobs you need
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Boss Selection */}
            <div>
              <Label className="text-blue-200">Boss</Label>
              <Select value={expeditionForm.boss_id} onValueChange={(v) => setExpeditionForm({ ...expeditionForm, boss_id: v })}>
                <SelectTrigger className="bg-black/40 border-blue-500/40 text-white" data-testid="expedition-boss-select">
                  <SelectValue placeholder="Select an expedition boss" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {expeditionBosses.map((boss) => (
                    <SelectItem key={boss.id} value={boss.id}>
                      {boss.name} (Lv.{boss.level})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ===== MESSAGE (moved to top) ===== */}
            <div>
              <Label className="text-blue-200">Your Offer / Message</Label>
              <Textarea
                placeholder="Write here what you offer to the party..."
                value={expeditionForm.message}
                onChange={(e) => setExpeditionForm({ ...expeditionForm, message: e.target.value })}
                className="bg-black/40 border-blue-500/40 text-white"
                rows={2}
                data-testid="expedition-message-input"
              />
            </div>

            {/* ===== AS ORGANIZER YOU PROVIDE ===== */}
            <div className="p-3 rounded-lg space-y-3" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)" }}>
              <Label className="text-green-300 text-sm font-semibold">As Organizer you provide:</Label>
              
              {/* Hyper Body */}
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={expeditionForm.hb_provided}
                  onCheckedChange={(c) => setExpeditionForm({ ...expeditionForm, hb_provided: c })}
                  className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-500"
                  data-testid="expedition-hb-checkbox"
                />
                <img src={HB_ICON} alt="HB" className="w-6 h-6" />
                <span className="text-green-200 text-sm">Hyper Body</span>
              </div>

              {/* HP Gear */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={expeditionForm.hp_gear_provided}
                    onCheckedChange={(c) => setExpeditionForm({ ...expeditionForm, hp_gear_provided: c, hp_gear_value: c ? expeditionForm.hp_gear_value : "" })}
                    className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-500"
                    data-testid="expedition-hp-gear-checkbox"
                  />
                  <img src={HP_GEAR_ICON} alt="HP Gear" className="w-6 h-6" />
                  <span className="text-green-200 text-sm">HP Gear</span>
                </div>
                {expeditionForm.hp_gear_provided && (
                  <div className="ml-8">
                    <Label className="text-green-300/70 text-xs">Total HP provided</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        step="100"
                        placeholder="e.g., 2000"
                        value={expeditionForm.hp_gear_value || ""}
                        onChange={(e) => setExpeditionForm({ ...expeditionForm, hp_gear_value: e.target.value })}
                        className="bg-black/40 border-green-500/40 text-white pr-12 h-8 text-sm"
                        data-testid="expedition-hp-gear-value"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-400/70 pointer-events-none">HP</span>
                    </div>
                  </div>
                )}
              </div>

              {/* 100% Safe Run */}
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={expeditionForm.safe_run_guaranteed}
                  onCheckedChange={(c) => setExpeditionForm({ ...expeditionForm, safe_run_guaranteed: c })}
                  className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-500"
                  data-testid="expedition-safe-run-checkbox"
                />
                <img src={SAFE_RUN_ICON} alt="Res" className="w-6 h-6" />
                <span className="text-green-200 text-sm">100% Safe Guaranteed Run</span>
              </div>
            </div>

            {/* ===== SELLING SERVICE ===== */}
            <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: expeditionForm.selling_service ? "rgba(234,179,8,0.1)" : "rgba(100,100,100,0.1)", border: `1px solid ${expeditionForm.selling_service ? "rgba(234,179,8,0.3)" : "rgba(100,100,100,0.3)"}` }}>
              <Checkbox
                checked={expeditionForm.selling_service}
                onCheckedChange={(c) => setExpeditionForm({ ...expeditionForm, selling_service: c, price_for_client: c ? expeditionForm.price_for_client : "", selected_wish_id: c ? expeditionForm.selected_wish_id : null })}
                className="data-[state=checked]:bg-yellow-600 data-[state=checked]:border-yellow-500"
                data-testid="expedition-selling-checkbox"
              />
              <DollarSign className="w-5 h-5 text-yellow-400" />
              <div>
                <span className="text-yellow-200 text-sm font-medium">Selling Service (allow buyers to join)</span>
                <p className="text-xs text-yellow-400/50">{expeditionForm.selling_service ? "Buyers can apply to join your expedition" : "No buyers — hunters only"}</p>
              </div>
            </div>

            {/* Time, Runtime & Price */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-blue-200">Expected Time</Label>
                <Input
                  type="datetime-local"
                  value={expeditionForm.expected_time}
                  min={new Date().toISOString().slice(0, 16)}
                  onChange={(e) => {
                    const selected = new Date(e.target.value);
                    if (selected <= new Date()) return;
                    setExpeditionForm({ ...expeditionForm, expected_time: e.target.value });
                  }}
                  className="bg-black/60 border-blue-400/60 text-white [color-scheme:dark] cursor-pointer"
                  style={{ colorScheme: "dark" }}
                  data-testid="expedition-time-input"
                />
                {/* Server Time (UK) display */}
                <div className="mt-1 space-y-0.5 p-2 rounded bg-black/30 border border-blue-500/20">
                  <p className="text-[10px] text-blue-300/90 font-semibold">
                    Server Time (UTC): <span className="text-blue-200 font-mono">{new Date().toLocaleString("en-GB", { timeZone: "UTC", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short", timeZoneName: "short" })}</span>
                  </p>
                  <p className="text-[10px] text-green-300/90 font-semibold">
                    Your Local Time: <span className="text-green-200 font-mono">{new Date().toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short", timeZoneName: "short" })}</span>
                  </p>
                  {expeditionForm.expected_time && (
                    <>
                      <p className="text-[10px] text-yellow-300/90">
                        Selected (Server/UTC): <span className="text-yellow-200 font-mono">{new Date(expeditionForm.expected_time).toLocaleString("en-GB", { timeZone: "UTC", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short", year: "numeric", timeZoneName: "short" })}</span>
                      </p>
                      <p className="text-[10px] text-green-300/70">
                        Selected (Your Time): <span className="text-green-200 font-mono">{new Date(expeditionForm.expected_time).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short", year: "numeric", timeZoneName: "short" })}</span>
                      </p>
                    </>
                  )}
                  <p className="text-[10px] text-gray-400/60">The calendar input uses your local timezone. The server runs on UTC time.</p>
                </div>
              </div>
              <div>
                <Label className="text-blue-200">Expected Runtime (min)</Label>
                <Input
                  type="number"
                  min="1"
                  max="600"
                  placeholder="e.g., 30"
                  value={expeditionForm.expected_runtime || ""}
                  onChange={(e) => setExpeditionForm({ ...expeditionForm, expected_runtime: e.target.value })}
                  className="bg-black/40 border-blue-500/40 text-white"
                  data-testid="expedition-runtime-input"
                />
                <p className="text-[10px] text-blue-400/50 mt-0.5">How long the run takes</p>
              </div>
            </div>

            {/* Buyer Price (only if selling) */}
            {expeditionForm.selling_service && (
              <div className="space-y-3">
                {/* Meso Price */}
                <div>
                  {/* Select Buyer from Queue */}
                  {expeditionForm.boss_id && expeditionWishes.filter(w => w.boss_id === expeditionForm.boss_id).length > 0 && (
                    <div className="mb-3">
                      <Label className="text-yellow-300 flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4" /> Pick Buyer from Queue
                      </Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {expeditionWishes
                          .filter(w => w.boss_id === expeditionForm.boss_id)
                          .slice(0, 5)
                          .map((wish) => {
                            // Check requirement mismatches
                            const mismatches = [];
                            if (wish.hb_required && !expeditionForm.hb_provided) mismatches.push("Hyper Body");
                            if (wish.hp_gear_required && !expeditionForm.hp_gear_provided) mismatches.push("HP Gear");
                            if (wish.safe_run_required && !expeditionForm.safe_run_guaranteed) mismatches.push("Safe Run");
                            const isSelected = expeditionForm.selected_wish_id === wish.id;
                            return (
                            <div
                              key={wish.id}
                              className={`p-2 rounded-lg cursor-pointer text-sm transition-all ${isSelected ? "ring-2 ring-yellow-400 bg-yellow-500/10" : "bg-black/30 hover:bg-yellow-500/5"}`}
                              onClick={() => {
                                if (!isSelected && mismatches.length > 0) {
                                  if (!window.confirm(`Buyer needs: ${mismatches.join(", ")} — but you do not offer ${mismatches.join(", ")}. Do you want to proceed?`)) return;
                                }
                                setExpeditionForm({ ...expeditionForm, selected_wish_id: isSelected ? null : wish.id, price_for_client: isSelected ? "" : wish.reward_offered });
                              }}
                              data-testid={`select-client-${wish.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-white">{wish.username}</span>
                                  <span className="text-yellow-400 ml-2">{formatMesos(wish.reward_offered)}</span>
                                  {wish.contact_ign && <span className="text-gray-400 ml-2 text-xs">IGN: {wish.contact_ign}</span>}
                                </div>
                                {isSelected && (
                                  <Badge className="bg-yellow-500/20 text-yellow-300 text-xs">Selected</Badge>
                                )}
                              </div>
                              {/* Buyer's requirements */}
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                {wish.hb_required && (
                                  <span className={`text-[10px] px-1 rounded ${expeditionForm.hb_provided ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                                    {expeditionForm.hb_provided ? "✓" : "✗"} Needs HB
                                  </span>
                                )}
                                {wish.hp_gear_required && (
                                  <span className={`text-[10px] px-1 rounded ${expeditionForm.hp_gear_provided ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                                    {expeditionForm.hp_gear_provided ? "✓" : "✗"} Needs HP Gear
                                  </span>
                                )}
                                {wish.safe_run_required && (
                                  <span className={`text-[10px] px-1 rounded ${expeditionForm.safe_run_guaranteed ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
                                    {expeditionForm.safe_run_guaranteed ? "✓" : "✗"} Needs Safe Run
                                  </span>
                                )}
                              </div>
                              {/* Buyer's message */}
                              {wish.message && (
                                <p className="text-[10px] text-gray-400 italic mt-1">"{wish.message}"</p>
                              )}
                              {/* Mismatch warning */}
                              {mismatches.length > 0 && (
                                <p className="text-[10px] text-red-400 mt-1">Missing: {mismatches.join(", ")}</p>
                              )}
                            </div>
                          );})}
                      </div>
                    </div>
                  )}
                  <Label className="text-blue-200">Buyer Price (M mesos){expeditionForm.selected_wish_id ? " (from buyer)" : ""}</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="e.g., 10"
                      value={expeditionForm.price_for_client}
                      onChange={(e) => setExpeditionForm({ ...expeditionForm, price_for_client: e.target.value })}
                      className="bg-black/40 border-blue-500/40 text-white pr-24"
                      data-testid="expedition-price-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-400/70 pointer-events-none">
                      {expeditionForm.price_for_client ? formatMesos(expeditionForm.price_for_client) : "M mesos"}
                    </span>
                  </div>
                </div>

                {/* Alternative Payment: APR */}
                <div className="p-3 rounded-lg space-y-2" style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.25)" }}>
                  <Label className="text-purple-300 text-xs font-semibold">Alternative Payment from Buyer</Label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={expeditionForm.apr_required}
                        onCheckedChange={(c) => setExpeditionForm({ ...expeditionForm, apr_required: c, apr_amount: c ? expeditionForm.apr_amount : "" })}
                        className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-500"
                        data-testid="expedition-apr-checkbox"
                      />
                      <img src={APR_ICON} alt="APR" className="w-6 h-6" />
                      <span className="text-purple-200 text-sm">APR (Ability Point Reset)</span>
                    </div>
                    {expeditionForm.apr_required && (
                      <div className="ml-8">
                        <Input
                          type="number"
                          min="1"
                          placeholder="Amount"
                          value={expeditionForm.apr_amount || ""}
                          onChange={(e) => setExpeditionForm({ ...expeditionForm, apr_amount: e.target.value })}
                          className="bg-black/40 border-purple-500/40 text-white h-8 text-sm w-28"
                          data-testid="expedition-apr-amount"
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={expeditionForm.onyx_apple_required}
                        onCheckedChange={(c) => setExpeditionForm({ ...expeditionForm, onyx_apple_required: c, onyx_apple_amount: c ? expeditionForm.onyx_apple_amount : "" })}
                        className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-500"
                        data-testid="expedition-onyx-checkbox"
                      />
                      <img src={ONYX_APPLE_ICON} alt="Onyx Apple" className="w-6 h-6" />
                      <span className="text-purple-200 text-sm">Onyx Apple</span>
                    </div>
                    {expeditionForm.onyx_apple_required && (
                      <div className="ml-8">
                        <Input
                          type="number"
                          min="1"
                          placeholder="Amount"
                          value={expeditionForm.onyx_apple_amount || ""}
                          onChange={(e) => setExpeditionForm({ ...expeditionForm, onyx_apple_amount: e.target.value })}
                          className="bg-black/40 border-purple-500/40 text-white h-8 text-sm w-28"
                          data-testid="expedition-onyx-amount"
                        />
                      </div>
                    )}
                  </div>
                  {(expeditionForm.apr_required || expeditionForm.onyx_apple_required) && expeditionForm.price_for_client && (
                    <p className="text-[10px] text-purple-300/60 italic mt-1">Items + mesos combined as payment</p>
                  )}
                </div>
              </div>
            )}

            {/* Slots */}
            <div className="flex items-center gap-2">
              <Label className="text-blue-200 text-sm">Slots:</Label>
              <Input
                type="number"
                min={1}
                max={30}
                value={expeditionForm.total_slots}
                onChange={(e) => setExpeditionForm({ ...expeditionForm, total_slots: parseInt(e.target.value) || 6 })}
                className="bg-black/40 border-blue-500/40 text-white w-16 text-center"
                data-testid="expedition-slots-input"
              />
              <span className="text-xs text-blue-400/60">(incl. you)</span>
            </div>

            {/* Available LFG Hunters for this boss */}
            {expeditionForm.boss_id && hunterLfgPosts.filter(lfg => (lfg.boss_ids || []).includes(expeditionForm.boss_id) || lfg.boss_id === expeditionForm.boss_id).length > 0 && (
              <div>
                <Label className="text-purple-300 flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4" /> Available Hunters (LFG) - Check to invite
                </Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {hunterLfgPosts
                    .filter(lfg => (lfg.boss_ids || []).includes(expeditionForm.boss_id) || lfg.boss_id === expeditionForm.boss_id)
                    .slice(0, 6)
                    .map((lfg) => (
                      <div key={lfg.id} className="flex items-center justify-between p-2 bg-black/30 rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={(expeditionForm.invite_lfg_ids || []).includes(lfg.user_id)}
                            onCheckedChange={(checked) => {
                              const current = expeditionForm.invite_lfg_ids || [];
                              if (checked) {
                                setExpeditionForm({ ...expeditionForm, invite_lfg_ids: [...current, lfg.user_id] });
                              } else {
                                setExpeditionForm({ ...expeditionForm, invite_lfg_ids: current.filter(id => id !== lfg.user_id) });
                              }
                            }}
                            className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                          />
                          <div>
                            <span className="text-purple-200 font-medium">{lfg.username}</span>
                            {lfg.hunter_reputation > 0 && <span className="text-yellow-400 ml-1 text-xs">({lfg.hunter_reputation} rep)</span>}
                            {lfg.has_hb && <Badge variant="outline" className="ml-1 text-xs py-0">HB</Badge>}
                            <div className="flex items-center gap-1 mt-0.5">
                              {(lfg.characters || []).map((char, ci) => {
                                const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                                return (
                                  <span key={ci} className="flex items-center gap-0.5 text-xs text-purple-300/70" title={`${char.name || char.job} Lv.${char.level}`}>
                                    {charSkills.slice(0, 1).map((s, si) => <img key={si} src={s.icon} alt={s.name} className="w-4 h-4" />)}
                                    Lv.{char.level}
                                  </span>
                                );
                              })}
                            </div>
                            {lfg.message && <p className="text-xs text-gray-500 italic">"{lfg.message}"</p>}
                          </div>
                        </div>
                        <span className="text-xs text-green-400">{(expeditionForm.invite_lfg_ids || []).includes(lfg.user_id) ? "Will Invite" : "Ready"}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Free for All toggle */}
            <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: "rgba(124,77,255,0.1)", border: "1px solid rgba(124,77,255,0.3)" }}>
              <Checkbox
                checked={expeditionForm.free_for_all}
                onCheckedChange={(c) => setExpeditionForm({ ...expeditionForm, free_for_all: c })}
                data-testid="expedition-ffa-checkbox"
              />
              <Label className="text-purple-200">Free For All (any job can apply)</Label>
            </div>

            {/* Job Requirements Section */}
            {!expeditionForm.free_for_all && (
              <div className="space-y-3">
                <Label className="text-blue-200 font-semibold">Looking For (Job Requirements)</Label>

                {/* Current requirements */}
                {expeditionForm.job_requirements.length > 0 && (
                  <div className="space-y-2">
                    {expeditionForm.job_requirements.map((req, i) => {
                      const jobData = availableJobs[req.job] || {};
                      const skills = jobData.skills || [];
                      return (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)" }}>
                          <div className="flex items-center gap-2">
                            {skills.map((skill, si) => (
                              <img key={si} src={skill.icon} alt={skill.name} className="w-6 h-6" title={skill.name} />
                            ))}
                            <span className="text-blue-100 font-medium">{req.job}</span>
                            <Badge className="bg-blue-500/30 text-blue-200 text-xs">x{req.count}</Badge>
                            {req.min_level && <Badge className="bg-purple-500/30 text-purple-200 text-xs">Lv.{req.min_level}+</Badge>}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            onClick={() => removeJobRequirement(i)}
                            data-testid={`remove-job-req-${i}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add new job requirement */}
                <div className="p-3 rounded-lg space-y-2" style={{ background: "rgba(0,0,0,0.3)", border: "1px dashed rgba(59,130,246,0.4)" }}>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-3 sm:col-span-1">
                      <Select value={newJobReq.job} onValueChange={(v) => setNewJobReq({ ...newJobReq, job: v })}>
                        <SelectTrigger className="bg-black/40 border-blue-500/40 text-white text-xs h-9" data-testid="new-job-select">
                          <SelectValue placeholder="Job class" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {Object.entries(availableJobs).map(([jobName, jobData]) => (
                            <SelectItem key={jobName} value={jobName}>
                              <div className="flex items-center gap-2">
                                {jobData.skills?.[0] && <img src={jobData.skills[0].icon} alt="" className="w-4 h-4" />}
                                {jobName}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Input
                        type="number" min={1} max={10} placeholder="Count"
                        value={newJobReq.count}
                        onChange={(e) => setNewJobReq({ ...newJobReq, count: parseInt(e.target.value) || 1 })}
                        className="bg-black/40 border-blue-500/40 text-white text-xs h-9"
                        data-testid="new-job-count"
                      />
                    </div>
                    <div>
                      <Input
                        type="number" min={1} max={255} placeholder="Min Lv"
                        value={newJobReq.min_level}
                        onChange={(e) => setNewJobReq({ ...newJobReq, min_level: e.target.value })}
                        className="bg-black/40 border-blue-500/40 text-white text-xs h-9"
                        data-testid="new-job-min-level"
                      />
                    </div>
                  </div>
                  <Button
                    size="sm" className="w-full text-xs"
                    style={{ background: "linear-gradient(180deg, #7c4dff 0%, #5c35cc 100%)", border: "1px solid #9c7cff" }}
                    onClick={addJobRequirement}
                    data-testid="add-job-requirement-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Job Requirement
                  </Button>
                </div>
              </div>
            )}

            {/* Leader Character Selection */}
            {userCharacters.length > 0 && (
              <div className="space-y-2">
                <Label className="text-blue-200 font-semibold">Your Characters (what you're bringing)</Label>
                <div className="space-y-1">
                  {userCharacters.map((char) => {
                    const isSelected = expeditionForm.leader_characters.some(c => c.id === char.id);
                    const charSkills = availableJobs[char.job]?.skills || [];
                    return (
                      <div
                        key={char.id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${isSelected ? "ring-1 ring-purple-400" : "hover:bg-purple-500/10"}`}
                        style={{
                          background: isSelected ? "rgba(124,77,255,0.2)" : "rgba(0,0,0,0.2)",
                          border: `1px solid ${isSelected ? "rgba(124,77,255,0.5)" : "rgba(124,77,255,0.15)"}`
                        }}
                        onClick={() => {
                          if (isSelected) {
                            setExpeditionForm({ ...expeditionForm, leader_characters: expeditionForm.leader_characters.filter(c => c.id !== char.id) });
                          } else {
                            setExpeditionForm({ ...expeditionForm, leader_characters: [...expeditionForm.leader_characters, { id: char.id, job: char.job, level: char.level, name: char.name }] });
                          }
                        }}
                        data-testid={`leader-char-${char.id}`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? "bg-purple-500 border-purple-400" : "border-purple-500/40"}`}>
                          {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <div className="flex items-center gap-1">
                          {charSkills.slice(0, 2).map((s, si) => (
                            <img key={si} src={s.icon} alt={s.name} className="w-5 h-5" />
                          ))}
                        </div>
                        <span className="text-blue-100 text-sm">{char.name || char.job}</span>
                        <span className="text-xs text-blue-400/60">Lv.{char.level}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateExpedition(false)} className="text-gray-300">Cancel</Button>
            <Button
              style={{ background: "linear-gradient(180deg, #7c4dff 0%, #5c35cc 100%)", border: "2px solid #9c7cff" }}
              onClick={handleCreateExpedition}
              data-testid="create-expedition-submit-btn"
            >
              <Users className="w-4 h-4 mr-2" /> Create Expedition
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
